import {
    AfterViewInit,
    Component,
    computed,
    ContentChildren,
    input,
    output,
    QueryList,
    signal,
    DestroyRef,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FlooriStepComponent } from '../floori-step/floori-step.component';
import { CommonModule } from '@angular/common';
import { FlooriIconComponent } from '../floori-icon/floori-icon.component';
import { StepperStyle, StepperType } from '../../models/stepper-type';
import { tap } from 'rxjs/operators';

@Component({
    selector: 'floori-stepper',
    standalone: true,
    imports: [CommonModule, FlooriIconComponent],
    templateUrl: './floori-stepper.component.html',
    styleUrl: './floori-stepper.component.scss'
})
export class FlooriStepperComponent implements AfterViewInit {
    @ContentChildren(FlooriStepComponent) steps!: QueryList<FlooriStepComponent>;
    private readonly destroyRef = inject(DestroyRef);

    readonly currentStep = signal(0);
    readonly stepsArray = signal<FlooriStepComponent[]>([]);

    readonly type = input<StepperType>('horizontal');
    readonly style = input<StepperStyle>('small');
    readonly stepChanged = output<number>();

    readonly allSteps = computed(() => {
        return this.stepsArray().map((step, index) => ({
            number: index + 1,
            title: step.title(),
            subtitle: step.subtitle(),
            isRequired: step.isRequired(),
            isActive: index === this.currentStep(),
            isCompleted: index < this.currentStep(),
            icon: index < this.currentStep() ? 'fl_check_circle_s' : 'fl_stop_circle_s',
        }));
    });

    readonly isVertical = computed(() => {
        return this.type() === 'vertical';
    });

    ngAfterViewInit() {
        this.refreshSteps();
        this.steps.changes
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(() => {
                    this.refreshSteps();
                }),
            )
            .subscribe();
    }

    refreshSteps() {
        const stepsArray = this.steps.toArray();
        this.stepsArray.set(stepsArray);

        if (this.currentStep() >= stepsArray.length) {
            this.currentStep.set(Math.max(0, stepsArray.length - 1));
        }

        stepsArray.forEach((step, index) => {
            step.isActive.set(index === this.currentStep());
        });
    }

    next() {
        if (this.currentStep() < this.steps.length - 1) {
            const stepsArray = this.steps.toArray();
            stepsArray[this.currentStep()].isActive.set(false);

            this.currentStep.update(prev => prev + 1);
            stepsArray[this.currentStep()].isActive.set(true);

            this.stepChanged.emit(this.currentStep());
        }
    }

    previous() {
        if (this.currentStep() > 0) {
            const stepsArray = this.steps.toArray();
            stepsArray[this.currentStep()].isActive.set(false);

            this.currentStep.update(prev => prev - 1);
            stepsArray[this.currentStep()].isActive.set(true);

            this.stepChanged.emit(this.currentStep());
        }
    }
}
